import logoImage from "@/assets/logo.png";
import bgImage from "@/assets/bg.jpg";
import { useRef, useState, useEffect } from "react";
import { calculateTimeLeft, getFormattedDate } from "@/lib/date-manipulation";
import {
  getEmploymentStatuses,
  getMaritalStatuses,
  getReferralData,
  getReligions,
  registerMember,
} from "@/services";
import { useSearchParams, useNavigate } from "react-router-dom";
import { convertFileToBase64 } from "@/lib/file";
import NotFound from "./NotFound";
import { toast } from "sonner";
import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const initialValue = {
  nik: "",
  name: "",
  birth_place: "",
  birth_date: "",
  religion: "",
  marital_status: "",
  address: "",
  employee_status: "",
  bpjs_kesehatan_id: "",
  bpjs_ketenagakerjaan_id: "",
  blood_type: "",
  skill: "",
};

export default function Registration() {
  // Handle avatar upload
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<"form" | "confirm" | "success">("form");

  const [timeLeft, setTimeLeft] = useState<any>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [avatar, setAvatar] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [form, setForm] = useState(initialValue);
  // const { mutate: submitRegisterData, isPending } = useRegisterMember();
  // const { data: statusMariage, isFetching: isFetchingMariageStatus } =
  //   useGetMaritalStatuses();
  // const { data: religions, isFetching: isFetchingReligions } =
  //   useGetReligions();
  // const { data: employmentStatuses } = useGetEmploymentStatus();
  // const { data: referral, isFetching: isFetchingReferral } =
  //   useGetInvitationData(searchParams.get("ref"));

  const [religions, setReligions] = useState([]);
  const [maritalStatus, setMaritalStatus] = useState([]);
  const [employmentStatus, setEmploymentStatus] = useState([]);
  const [referral, setReferral] = useState(null);

  // Form state
  const [submitting, setSubmitting] = useState(false);
  
  // Image crop states
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement>(null);
  const [showCropModal, setShowCropModal] = useState(false);

  useEffect(() => {
    if (searchParams.get("ref")) {
      getIntialData();
    }
  }, [searchParams.get("ref")]);

  useEffect(() => {
    if (referral) {
      if (timeLeft <= 0) return;
      const timer = setInterval(
        () => setTimeLeft(calculateTimeLeft(referral?.expired_at + "Z")),
        1000
      );
      return () => clearInterval(timer);
    }
  }, [timeLeft]);

  useEffect(() => {
    if (referral) {
      setTimeLeft(calculateTimeLeft(referral?.expired_at + "Z"));
    }
  }, [referral]);

  const getIntialData = async () => {
    getReferralData(searchParams.get("ref")).then((resp) => {
      setReferral(resp.data?.data);
    });
    getReligions().then((resp) => {
      setReligions(resp.data?.data);
    });
    getMaritalStatuses().then((resp) => {
      setMaritalStatus(resp.data?.data);
    });

    getEmploymentStatuses().then((resp) => {
      setEmploymentStatus(resp.data?.data);
    });
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        1,
        width,
        height
      ),
      width,
      height
    );
    setCrop(crop);
  };

  // Validate file type, size and extension
  const validateImageFile = (file: File): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Check file size (max 4MB)
    const maxSize = 4 * 1024 * 1024; // 4MB in bytes
    if (file.size > maxSize) {
      errors.push('Ukuran file terlalu besar. Maksimal 2MB.');
    }
    
    // Check file extension
    const allowedExtensions = ['jpg', 'jpeg', 'png'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      errors.push('Format file tidak didukung. Gunakan JPG, JPEG, atau PNG.');
    }
    
    // Check MIME type
    const allowedMimeTypes = ['image/jpeg', 'image/png'];
    if (!allowedMimeTypes.includes(file.type)) {
      errors.push('Tipe file tidak valid. Gunakan file gambar.');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        toast.error(`Error upload foto: ${validation.errors.join(' ')}`);
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      // Additional content validation for images
      try {
        const isValidImage = await validateImageContent(file);
        if (!isValidImage) {
          toast.error('File tidak valid. Pastikan file adalah gambar asli.');
          // Clear the file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }
      } catch (error) {
        toast.error('Gagal memvalidasi file. Silakan coba lagi.');
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      setAvatarFile(file);
      
      const reader = new FileReader();
      reader.onload = (ev) => {
        setAvatar(ev.target?.result as string);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Validate image content to ensure it's actually an image
  const validateImageContent = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        // If image loaded successfully, it's a valid image
        resolve(true);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        // If there was an error loading, it's not a valid image
        resolve(false);
      };
      
      img.src = objectUrl;
    });
  };

  const handleCropComplete = (crop: PixelCrop) => {
    setCompletedCrop(crop);
  };

  const getCroppedImg = async (): Promise<Blob | null> => {
    if (!imgRef.current || !completedCrop) return null;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return null;

    const pixelRatio = window.devicePixelRatio;
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    
    canvas.width = Math.floor(completedCrop.width * scaleX * pixelRatio);
    canvas.height = Math.floor(completedCrop.height * scaleY * pixelRatio);
    
    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = 'high';

    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;

    ctx.save();
    
    // Move the crop origin to the center of the image
    ctx.translate(-cropX, -cropY);
    
    // Draw the image
    ctx.drawImage(
      imgRef.current,
      0,
      0,
      imgRef.current.naturalWidth,
      imgRef.current.naturalHeight,
      0,
      0,
      imgRef.current.naturalWidth,
      imgRef.current.naturalHeight
    );

    ctx.restore();

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.8);
    });
  };

  const handleCropConfirm = async () => {
    if (!completedCrop || !imgRef.current) return;
    
    const croppedBlob = await getCroppedImg();
    if (croppedBlob) {
      // Convert blob to file
      const croppedFile = new File([croppedBlob], 'cropped_avatar.jpg', { type: 'image/jpeg' });
      
      // Compress the image to ensure it's under 80KB
      const compressedFile = await compressImage(croppedFile);
      
      // Set the compressed file as avatar file
      setAvatarFile(compressedFile);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(compressedFile);
      setAvatar(previewUrl);
      
      setShowCropModal(false);
    }
  };

  const compressImage = async (file: File): Promise<File> => {
    // Check if file is already under 80KB
    if (file.size <= 80 * 1024) {
      return file;
    }
    
    // Create canvas for compression
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      return file;
    }
    
    // Create image from file
    const img = new Image();
    const dataUrl = await convertFileToBase64(file);
    img.src = dataUrl as string;
    
    // Wait for image to load
    await new Promise((resolve) => {
      img.onload = resolve;
    });
    
    // Set canvas dimensions (reduce to compress)
    const maxSize = 300; // Maximum dimension
    let { width, height } = img;
    
    if (width > height) {
      if (width > maxSize) {
        height *= maxSize / width;
        width = maxSize;
      }
    } else {
      if (height > maxSize) {
        width *= maxSize / height;
        height = maxSize;
      }
    }
    
    canvas.width = width;
    canvas.height = height;
    
    // Draw image on canvas
    ctx.drawImage(img, 0, 0, width, height);
    
    // Convert to blob with compression
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          // Use the original file name but with .jpg extension
          const fileName = file.name.replace(/\.[^/.]+$/, ".jpg");
          const compressedFile = new File([blob], fileName, { type: 'image/jpeg' });
          resolve(compressedFile);
        } else {
          resolve(file);
        }
      }, 'image/jpeg', 0.7); // Quality setting for compression
    });
  };

  // Handle form change
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    // Validate required fields
    const requiredFields = [
      "name",
      "birth_place",
      "birth_date",
      "religion",
      "marital_status",
      "address",
      "employee_status",
      "bpjs_kesehatan_id",
      "bpjs_ketenagakerjaan_id",
    ];
    const missingFields = requiredFields.filter(
      (field) => !form[field as keyof typeof form]
    );

    if (missingFields.length > 0) {
      alert(`Field berikut wajib diisi: ${missingFields.join(", ")}`);
      setSubmitting(false);
      return;
    }

    if (!searchParams?.get("ref")) {
      alert(`Kode referral tidak ditemukan`);
      setSubmitting(false);
      return;
    }

    let photo_profile = null;
    if (avatarFile) {
      try {
        photo_profile = await convertFileToBase64(avatarFile);
        // Extract base64 data from data URL
        if (typeof photo_profile === 'string' && photo_profile.includes('base64,')) {
          photo_profile = photo_profile.split('base64,')[1];
        } else {
          // If it's already base64 data, use it as is
          photo_profile = photo_profile as string;
        }
      } catch (error) {
        console.error("Error converting file to base64:", error);
        toast.error("Gagal memproses foto profil. Silakan coba lagi.");
        setSubmitting(false);
        return;
      }
    }

    try {
      // Prepare registration data
      const registrationData: any = {
        name: form.name,
        birth_date: form.birth_date,
        birth_place: form.birth_place,
        religion: form.religion,
        marital_status: form.marital_status,
        address: form.address,
        employment_status: form.employee_status,
        bpjs_kesehatan_id: form.bpjs_kesehatan_id,
        bpjs_ketenagakerjaan_id: form.bpjs_ketenagakerjaan_id,
        blood_type: form.blood_type,
        skill: form.skill,
        photo_profile: photo_profile,
        referral_code: searchParams.get("ref"),
      };

      // Only include NIK if it has a value
      if (form.nik && form.nik.trim() !== '') {
        registrationData.nik = form.nik;
      }

      await registerMember(referral?.referral_code, registrationData);
      
      // Reset form
      setForm(initialValue);
      setAvatar(null);
      setAvatarFile(null);
      
      // Show success toast
      toast.success("Pendaftaran Anda telah berhasil dikirim dan akan segera diproses");
      
      // Redirect to home page after a short delay
      setTimeout(() => {
        navigate("/");
      }, 2000); // 2 second delay to show the toast
      
    } catch (error: any) {
      console.error("Error submitting registration:", error);
      
      // Handle specific error cases
      let errorMessage = "Gagal mengirim pendaftaran. Silakan coba lagi. Pastikan No. BPJS dan BPJSTK belum pernah didaftarkan";
      
      if (error.response?.data) {
        const { data } = error.response;
        
        // Check for specific error messages from API
        if (data.message) {
          if (data.message.includes('bpjs_kesehatan_id') || data.message.includes('BPJS Kesehatan')) {
            errorMessage = "Nomor BPJS Kesehatan sudah pernah digunakan. Silakan gunakan nomor lain.";
          } else if (data.message.includes('bpjs_ketenagakerjaan_id') || data.message.includes('BPJS Ketenagakerjaan')) {
            errorMessage = "Nomor BPJS Ketenagakerjaan sudah pernah digunakan. Silakan gunakan nomor lain.";
          } else if (data.message.includes('nik') || data.message.includes('NIK')) {
            errorMessage = "NIK sudah pernah digunakan. Silakan periksa kembali NIK Anda.";
          } else if (data.message.includes('duplicate') || data.message.includes('already exists')) {
            errorMessage = "Data yang Anda masukkan sudah pernah terdaftar. Silakan periksa kembali.";
          } else if (data.message.includes('validation') || data.message.includes('invalid')) {
            errorMessage = "Data yang Anda masukkan tidak valid. Silakan periksa kembali.";
          } else if (data.message.includes('referral') || data.message.includes('kode referral')) {
            errorMessage = "Kode referral tidak valid atau sudah tidak berlaku.";
          } else {
            // Use the API error message if it's user-friendly
            errorMessage = data.message;
          }
        } else if (data.errors) {
          // Handle validation errors object
          const firstError = Object.values(data.errors)[0];
          if (Array.isArray(firstError) && firstError.length > 0) {
            errorMessage = firstError[0] as string;
          }
        }
      } else if (error.response?.status === 422) {
        errorMessage = "Data yang Anda masukkan tidak valid. Silakan periksa kembali semua field.";
      } else if (error.response?.status === 409) {
        errorMessage = "Data sudah pernah terdaftar. Silakan periksa kembali informasi Anda.";
      } else if (error.response?.status === 400) {
        errorMessage = "Permintaan tidak valid. Silakan periksa kembali data yang Anda masukkan.";
      } else if (error.response?.status >= 500) {
        errorMessage = "Terjadi kesalahan pada server. Silakan coba lagi dalam beberapa saat.";
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        errorMessage = "Tidak dapat terhubung ke server. Silakan periksa koneksi internet Anda.";
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => setStep("form");
  const handleFinalSubmit = () => setStep("success");

  if (!searchParams.get("ref") || !referral) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-union-red/5"></div>
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${bgImage})` }}
      ></div>
      {/* Main Content */}
      <div className="relative min-h-screen flex flex-col">
        {/* Header */}
        <header className="absolute top-0 left-0 right-0 z-10 p-6">
          <div className="container mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src={logoImage}
                  alt="Logo"
                  className="h-8 w-8 h-auto object-cover"
                />
                <span className="text-xl font-bold text-foreground">
                  FSP LEM SPSI
                </span>
              </div>
            </div>
          </div>
        </header>
        {/* Registration Form Centered */}
        <main className="flex-1 flex items-center justify-center px-2 py-8">
          <div className="w-full max-w-3xl bg-white/90 rounded-xl shadow-lg p-4 sm:p-8 flex flex-col items-center gap-4 mt-24 mb-12">
            {/* Logo */}
            <img
              src={logoImage}
              alt="Logo"
              className="h-14 w-14 object-cover mb-2"
            />
            {/* Judul */}
            <h2 className="text-2xl font-bold text-center mb-1">
              Formulir Pendaftaran FSP LEM SPSI
            </h2>
            <div className="text-md font-semibold text-muted-foreground text-center mb-2">
              PUK: {referral?.puk_name || '-'}, DPC: {referral?.dpc_name || '-'}
            </div>
            {/* Info Digenerate oleh & Sisa waktu/antrian dua kolom */}
            <div className="w-full flex flex-col sm:flex-row gap-4 mb-2">
              {/* Border wrapper */}
              <div className="flex-1 border border-muted rounded-lg p-3 bg-white/70 mb-2 sm:mb-0">
                {/* Digenerate oleh */}
                <div className="flex flex-col items-center sm:items-start gap-1">
                  <div className="text-xs text-muted-foreground">
                    Digenerate oleh
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                       {referral?.author?.[0] || "Admin"}
                    </div>
                    <div className="font-semibold">
                     {referral?.author || "Admin"}
                      <div className="text-xs text-muted-foreground">
                        {getFormattedDate(referral?.created_at + "Z")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-1 border border-muted rounded-lg p-3 bg-white/70">
                {/* Sisa waktu & Antrian */}
                <div className="flex flex-col items-center sm:items-end gap-1">
                  <div className="text-muted-foreground">
                    Antrian #{referral?.used+1} dari #{referral?.quota}
                  </div>
                  <div className="text-red-600 font-semibold">
                    Sisa waktu:{" "}
                    {`${
                      timeLeft?.days ? timeLeft?.days + " hari " : ""
                    }${timeLeft?.hours
                      .toString()
                      .padStart(2, "0")}:${timeLeft?.minutes
                      .toString()
                      .padStart(2, "0")}:${timeLeft?.seconds
                      .toString()
                      .padStart(2, "0")}`}
                  </div>
                </div>
              </div>
            </div>
            {/* Step: Formulir atau Konfirmasi */}
            {step === "form" && (
              <form
                className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 mt-2"
                onSubmit={handleSubmit}
              >
                {/* Upload foto profil sebagai avatar preview (full width) */}
                <div className="md:col-span-2 flex flex-col items-center gap-2 mb-2">
                  <label className="text-sm font-medium">Foto Profil</label>
                  <div
                    className="w-20 h-20 rounded-full bg-gray-200 border-1 border-primary flex items-center justify-center cursor-pointer overflow-hidden hover:opacity-80 transition"
                    onClick={() => fileInputRef.current?.click()}
                    title="Klik untuk upload foto"
                  >
                    {avatar ? (
                      <img
                        src={avatar}
                        alt="Avatar Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-400 text-3xl font-bold">
                        +
                      </span>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="foto"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />
                  <span className="text-xs text-muted-foreground">
                    Klik avatar untuk upload foto
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nama</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    type="text"
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="Nama Lengkap"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Tempat Lahir
                  </label>
                  <input
                    name="birth_place"
                    value={form.birth_place}
                    onChange={handleChange}
                    type="text"
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="Tempat Lahir"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Tanggal Lahir
                  </label>
                  <input
                    name="birth_date"
                    value={form.birth_date}
                    onChange={handleChange}
                    type="date"
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="dd/mm/yyyy"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    NIK{" "}
                    <span className="text-xs text-muted-foreground">
                      (Optional)
                    </span>
                  </label>
                  <input
                    name="nik"
                    value={form.nik}
                    onChange={handleChange}
                    type="text"
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="NIK"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Agama
                  </label>
                  <select
                    name="religion"
                    value={form.religion}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Pilih agama</option>
                    {religions?.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}

                   {/* <option value="islam">Islam</option>
                    <option value="kristen">
                      Kristen
                    </option>
                    <option value="katolik">Katolik</option>
                    <option value="hindu">Hindu</option>
                    <option value="buddha">Buddha</option>
                    <option value="konghucu">Konghucu</option>
                    */}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Status Keluarga
                  </label>
                  <select
                    name="marital_status"
                    value={form.marital_status}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Pilih status</option>
                      {maritalStatus?.map((item) => (
                        <option value={item} key={item}>
                          {item}
                        </option>
                      ))}
                    {/*<option value="belum kawin">Belum Kawin</option>
                    <option value="kawin">Kawin</option>
                    <option value="cerai hidup">Cerai Hidup</option>
                    <option value="cerai mati">Cerai Mati</option> */}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Alamat Lengkap (sesuai KTP)
                  </label>
                  <textarea
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="Alamat sesuai KTP"
                    rows={2}
                    required
                  ></textarea>
                </div>
                {/* <div>
                  <label className="block text-sm font-medium mb-1">
                    Nama Perusahaan
                  </label>
                  <input
                    name="perusahaan"
                    value="PT Anugerah Jaya"
                    onChange={handleChange}
                    type="text"
                    className="w-full border rounded px-3 py-2 text-sm"
                    disabled
                    placeholder="Nama Perusahaan"
                  />
                </div> */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Status Karyawan
                  </label>
                  <select
                    name="employee_status"
                    value={form.employee_status}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Pilih status</option>
                    {/* {employmentStatus?.map((item) => (
                      <option value={item} key={item}>
                        {item}
                      </option>
                    ))} */}
                    <option value="tetap">Tetap</option>
                    <option value="kontrak">Kontrak</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    No. BPJS Kesehatan{" "}
                  </label>
                  <input
                    name="bpjs_kesehatan_id"
                    value={form.bpjs_kesehatan_id}
                    onChange={handleChange}
                    type="text"
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="No. BPJS Kesehatan"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    No. BPJS Tenaga Kerja{" "}
                  </label>
                  <input
                    name="bpjs_ketenagakerjaan_id"
                    value={form.bpjs_ketenagakerjaan_id}
                    onChange={handleChange}
                    type="text"
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="No. BPJS Tenaga Kerja"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Golongan Darah
                  </label>
                  <select
                    name="blood_type"
                    value={form.blood_type}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Pilih golongan darah</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="AB">AB</option>
                    <option value="O">O</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Keahlian Lainnya{" "}
                    <span className="text-xs text-muted-foreground">
                      (Optional)
                    </span>
                  </label>
                  <textarea
                    name="skill"
                    value={form.skill}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="Keahlian Lainnya"
                    rows={2}
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`mt-2 w-full font-semibold py-2 rounded transition ${
                    submitting 
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                      : 'bg-primary text-white hover:bg-primary/90'
                  }`}
                >
                  {submitting ? 'Processing...' : 'Daftar'}
                </button>
              </form>
            )}
            {step === "confirm" && (
              <div className="w-full flex flex-col gap-4 mt-2">
                <div className="text-lg font-bold text-center mb-2">
                  Konfirmasi Data Pendaftaran
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-20 h-20 rounded-full bg-gray-200 border-2 border-primary flex items-center justify-center overflow-hidden">
                    {avatar ? (
                      <img
                        src={avatar}
                        alt="Avatar Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-400 text-3xl font-bold">
                        +
                      </span>
                    )}
                  </div>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">Nama</td>
                      <td className="py-1 text-right font-semibold">
                        {form.name}
                      </td>
                    </tr>
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">
                        Tempat Lahir
                      </td>
                      <td className="py-1 text-right font-semibold">
                        {form.birth_place}
                      </td>
                    </tr>
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">
                        Tanggal Lahir
                      </td>
                      <td className="py-1 text-right font-semibold">
                        {getFormattedDate(form.birth_date + "Z")}
                      </td>
                    </tr>
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">NIK</td>
                      <td className="py-1 text-right font-semibold">
                        {form.nik}
                      </td>
                    </tr>
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">Agama</td>
                      <td className="py-1 text-right font-semibold">
                        {form.religion}
                      </td>
                    </tr>
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">
                        Status Keluarga
                      </td>
                      <td className="py-1 text-right font-semibold">
                        {form.marital_status}
                      </td>
                    </tr>
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">
                        Alamat
                      </td>
                      <td className="py-1 text-right font-semibold">
                        {form.address}
                      </td>
                    </tr>
                    {/* <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">
                        Nama Perusahaan
                      </td>
                      <td className="py-1 text-right font-semibold">
                        {form.perusahaan}
                      </td>
                    </tr> */}
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">
                        Status Karyawan
                      </td>
                      <td className="py-1 text-right font-semibold">
                        {form.employee_status}
                      </td>
                    </tr>
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">
                        No. BPJS Kesehatan
                      </td>
                      <td className="py-1 text-right font-semibold">
                        {form.bpjs_kesehatan_id}
                      </td>
                    </tr>
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">
                        No. BPJS Tenaga Kerja
                      </td>
                      <td className="py-1 text-right font-semibold">
                        {form.bpjs_ketenagakerjaan_id}
                      </td>
                    </tr>
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">
                        Golongan Darah
                      </td>
                      <td className="py-1 text-right font-semibold">
                        {form.blood_type}
                      </td>
                    </tr>
                    <tr className="border-b border-muted hover:bg-primary/5 transition">
                      <td className="pr-2 py-1 text-muted-foreground">
                        Keahlian Lainnya
                      </td>
                      <td className="py-1 text-right font-semibold">
                        {form.skill}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={handleBack}
                    className="px-4 py-2 rounded bg-muted text-foreground font-semibold hover:bg-muted/80 transition"
                  >
                    Kembali
                  </button>
                  <button
                    onClick={handleFinalSubmit}
                    className="px-4 py-2 rounded bg-primary text-white font-semibold hover:bg-primary/90 transition"
                  >
                    Kirim Sekarang
                  </button>
                </div>
              </div>
            )}
            {step === "success" && (
              <div className="w-full flex flex-col items-center gap-4 mt-2">
                <div className="text-lg font-bold text-center mb-2 text-green-700">
                  Pendaftaran berhasil dikirim!
                </div>
              </div>
            )}
          </div>
        </main>
        {/* Footer */}
        <footer className="relative z-10 border-t bg-card/50 backdrop-blur-sm">
          <div className="container mx-auto px-6 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3"></div>
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <span>
                  All right reserved © {new Date().getFullYear()} FSP LEM SPSI
                </span>
              </div>
            </div>
          </div>
        </footer>
        
        {/* Crop Modal */}
        {showCropModal && avatar && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-hidden">
            <div className="bg-white rounded-lg p-4 max-w-2xl w-full max-h-[90vh] flex flex-col">
              <h3 className="text-lg font-semibold mb-4">Crop Foto Profil</h3>
              <div className="flex-grow overflow-auto flex items-center justify-center">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={handleCropComplete}
                  aspect={1}
                  circularCrop
                >
                  <img
                    ref={imgRef}
                    src={avatar}
                    alt="Crop preview"
                    onLoad={onImageLoad}
                    className="max-h-[60vh] max-w-full object-contain"
                  />
                </ReactCrop>
              </div>
              <div className="flex gap-3 mt-4 justify-center">
                <button
                  onClick={() => setShowCropModal(false)}
                  className="px-4 py-2 rounded bg-gray-300 text-gray-800 font-semibold hover:bg-gray-400 transition"
                >
                  Batal
                </button>
                <button
                  onClick={handleCropConfirm}
                  className="px-4 py-2 rounded bg-primary text-white font-semibold hover:bg-primary/90 transition"
                >
                  Konfirmasi Crop
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
